import React, { useState, useRef } from 'react';
import { Upload, X, FileText } from 'lucide-react';

/**
 * Reusable File Upload Component with Drag & Drop
 *
 * @param {Object} props
 * @param {Function} props.onFilesChange - Callback when files are added/removed
 * @param {Array} props.files - Current files array
 * @param {boolean} props.multiple - Allow multiple file uploads (default: true)
 * @param {string} props.accept - File types to accept (e.g., ".pdf,.docx,.xlsx")
 * @param {boolean} props.showFileList - Show the list of uploaded files (default: true)
 * @param {string} props.maxSize - Max file size description (optional, for display only)
 */
export default function FileUpload({
  onFilesChange,
  files = [],
  multiple = true,
  accept = ".pdf,.docx,.xlsx,.xls,.pptx,.ppt,image/*",
  showFileList = true,
  maxSize = null,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    if (multiple) {
      onFilesChange([...files, ...droppedFiles]);
    } else {
      onFilesChange([droppedFiles[0]]);
    }
  };

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (multiple) {
      onFilesChange([...files, ...selectedFiles]);
    } else {
      onFilesChange([selectedFiles[0]]);
    }
  };

  const removeFile = (index) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          isDragging
            ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 scale-[1.02]'
            : 'border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 bg-gradient-to-br from-blue-50/30 to-purple-50/30 dark:from-blue-900/10 dark:to-purple-900/10 hover:bg-gradient-to-br hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
        />

        <div className={`mx-auto mb-3 w-12 h-12 rounded-full bg-gradient-to-br flex items-center justify-center ${
          isDragging ? 'from-blue-500 to-purple-600' : 'from-blue-400 to-purple-500'
        }`}>
          <Upload className="text-white" size={24} />
        </div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {isDragging ? 'Drop files here' : 'Drop files or click to upload'}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {accept.includes('pdf') && 'PDF, '}
          {accept.includes('docx') && 'Word, '}
          {accept.includes('xlsx') && 'Excel, '}
          {accept.includes('pptx') && 'PowerPoint, '}
          {accept.includes('image') && 'Images'}
        </p>
        {maxSize && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Max size: {maxSize}</p>
        )}
      </div>

      {showFileList && files.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
          {files.map((file, idx) => (
            <div
              key={idx}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-sm"
            >
              <FileText size={14} />
              <span className="max-w-xs truncate">{file.name}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(idx);
                }}
                className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
